import { Controller } from "stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["fileInput", "fileList", "submitBtn", "dropzone"]

  connect() {
    this.uploadedBlobs = []
    this.activeUploads = 0
    this.dragCounter = 0
    this.maxSize = 100 * 1024 * 1024
    this.allowedExtensions = [".pdf", ".txt", ".md", ".csv", ".json", ".docx"]
    // Preserve the original button text for context-appropriate labeling
    this.defaultButtonText = this.hasSubmitBtnTarget ? this.submitBtnTarget.textContent.trim() : "Create Project"
  }

  dragover(e) {
    e.preventDefault()
  }

  dragenter(e) {
    e.preventDefault()
    this.dragCounter++
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add("dropzone-active")
    }
  }

  dragleave(e) {
    e.preventDefault()
    this.dragCounter--
    if (this.dragCounter === 0 && this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("dropzone-active")
    }
  }

  drop(e) {
    e.preventDefault()
    this.dragCounter = 0
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove("dropzone-active")
    }
    this.uploadFiles(e.dataTransfer.files)
  }

  selectFiles(e) {
    this.uploadFiles(e.target.files)
    e.target.value = ""
  }

  uploadFiles(fileList) {
    Array.from(fileList).forEach(file => {
      if (!this.validateFile(file)) return

      const uploadId = this.generateId(file)
      this.activeUploads++
      this.updateSubmitState()
      this.insertFileRow(file, uploadId)

      const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads", {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            const pct = Math.round((event.loaded / event.total) * 100)
            this.updateProgress(uploadId, pct)
          })
        }
      })

      upload.create((error, blob) => {
        this.activeUploads--
        if (error) {
          this.markError(uploadId, error)
        } else {
          this.markComplete(uploadId, blob.signed_id)
          this.uploadedBlobs.push(blob)
          this.insertHiddenInput(blob)
        }
        this.updateSubmitState()
      })
    })
  }

  validateFile(file) {
    const ext = "." + file.name.split(".").pop().toLowerCase()
    if (!this.allowedExtensions.includes(ext)) {
      alert(`${file.name}: Unsupported file type. Allowed: PDF, TXT, Markdown, CSV, JSON, Word.`)
      return false
    }
    if (file.size > this.maxSize) {
      alert(`${file.name}: File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 100 MB.`)
      return false
    }
    return true
  }

  insertFileRow(file, uploadId) {
    if (!this.hasFileListTarget) return

    const size = file.size < 1024 * 1024
      ? `${(file.size / 1024).toFixed(0)} KB`
      : `${(file.size / 1024 / 1024).toFixed(1)} MB`

    // Escape filename to prevent XSS
    const safeName = file.name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")

    this.fileListTarget.insertAdjacentHTML("beforeend", `
      <div id="upload-${uploadId}" class="d-flex align-items-center py-2 px-3 mb-2 border rounded">
        <i class="fal fa-file mr-3 text-muted"></i>
        <div class="flex-fill">
          <div class="font-size-14 font-weight-bold">${safeName}</div>
          <div class="font-size-12 text-muted">
            <span>${size}</span>
            <span class="ml-2 upload-status">Uploading...</span>
          </div>
          <div class="progress mt-1" style="height: 4px;">
            <div class="progress-bar bg-success upload-bar" style="width: 0%;"></div>
          </div>
        </div>
        <button type="button" class="btn btn-sm text-muted ml-2 upload-remove d-none" data-action="click->project-creation-upload#removeFile" data-signed-id="" style="font-size: 16px;">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `)
  }

  updateProgress(uploadId, pct) {
    const row = document.getElementById(`upload-${uploadId}`)
    if (!row) return
    const bar = row.querySelector(".upload-bar")
    const status = row.querySelector(".upload-status")
    if (bar) bar.style.width = `${pct}%`
    if (status) status.textContent = `${pct}%`
  }

  markComplete(uploadId, signedId) {
    const row = document.getElementById(`upload-${uploadId}`)
    if (!row) return
    const bar = row.querySelector(".upload-bar")
    const status = row.querySelector(".upload-status")
    const removeBtn = row.querySelector(".upload-remove")
    if (bar) bar.style.width = "100%"
    if (status) { status.textContent = "Ready"; status.classList.add("text-success") }
    if (removeBtn) { removeBtn.classList.remove("d-none"); removeBtn.dataset.signedId = signedId }

    // Hide progress bar after complete
    const progress = row.querySelector(".progress")
    if (progress) progress.classList.add("d-none")
  }

  markError(uploadId, error) {
    const row = document.getElementById(`upload-${uploadId}`)
    if (!row) return
    const status = row.querySelector(".upload-status")
    const bar = row.querySelector(".upload-bar")
    if (status) { status.textContent = "Failed"; status.classList.add("text-danger") }
    if (bar) bar.classList.replace("bg-success", "bg-danger")
  }

  removeFile(e) {
    const signedId = e.currentTarget.dataset.signedId
    if (!signedId) return

    this.uploadedBlobs = this.uploadedBlobs.filter(b => b.signed_id !== signedId)

    // Remove hidden input (find by iterating to avoid CSS selector issues with signed_id characters)
    const inputs = this.element.querySelectorAll('input[name="file_signed_ids[]"]')
    inputs.forEach(input => { if (input.value === signedId) input.remove() })

    // Remove row (find parent with upload-* id)
    const row = e.currentTarget.closest("[id^='upload-']")
    if (row) row.remove()

    this.updateSubmitState()
  }

  insertHiddenInput(blob) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "file_signed_ids[]"
    input.value = blob.signed_id
    this.element.appendChild(input)
  }

  updateSubmitState() {
    if (!this.hasSubmitBtnTarget) return

    if (this.activeUploads > 0) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.textContent = `Uploading (${this.activeUploads} remaining)...`
    } else if (this.uploadedBlobs.length === 0) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.textContent = this.defaultButtonText
    } else {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = `${this.defaultButtonText} (${this.uploadedBlobs.length} ${this.uploadedBlobs.length === 1 ? "file" : "files"})`
    }
  }

  generateId(file) {
    return `${file.name.replace(/\W/g, "")}-${file.lastModified}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
  }
}
